// @givmo/social — one post as the history and its own page show it (SMM-WEB1; §SMM-8 point 2,
// "History"): its words, image and time, then each account's post with its state in a plain word
// (draft, scheduled, posting, published, didn't post), a failure's reason in the organization's
// terms, a deferral's note, the permalink, Retry on a failed one and Cancel on what is scheduled.
//
// ⚠ "COULDN'T CONFIRM IT PUBLISHED" IS ITS OWN STATE, never folded into "didn't post": a send-once
// platform's unconfirmed send may be live, so its Retry asks first (a confirm that says to check the
// account), and its line links to the account. A post too late for its time asks first too: it was
// written for that time. Retry shows only where the server says a Retry can cure the failure
// (`can_retry`): refused words, a vanished image or an author who still can't post go out only as
// a new post, and their lines say so.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  firstErrorCode,
  isGivmoError,
  sdk,
  type SocialPostOut,
  type SocialVariantOut,
} from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, cn, testIdProp, toast } from '@givmo/design-system';
import { PlatformMark } from './platform-mark';
import { platformLabel } from './social-platforms';
import { useRefreshSocial } from './social-queries';
import { keepRescheduleTime } from './social-reschedule';
import { socialPath } from './social-routes';
import { whenWords } from './social-ui';
import { failureWords, socialWords as w } from './social-words';

/** A variant's state as the history says it. */
export type VariantState =
  | 'draft'
  | 'scheduled'
  | 'deferred'
  | 'posting'
  | 'published'
  | 'unknown'
  | 'failed'
  | 'removed';

export function variantState(variant: SocialVariantOut): VariantState {
  switch (variant.status) {
    case 'draft':
      return 'draft';
    case 'queued':
      return variant.deferred_reason ? 'deferred' : 'scheduled';
    case 'publishing':
      return 'posting';
    case 'published':
      return 'published';
    case 'failed':
      return variant.failure_reason === 'outcome_unknown' ? 'unknown' : 'failed';
    case 'removed':
      return 'removed';
    default:
      return 'failed';
  }
}

const STATE_WORDS: Record<VariantState, string> = {
  draft: w.statusDraft,
  scheduled: w.statusQueued,
  deferred: w.statusQueued,
  posting: w.statusPublishing,
  published: w.statusPublished,
  unknown: w.statusUnknown,
  failed: w.statusFailed,
  removed: w.statusRemoved,
};

/** Only the states a person must act on carry a colour; the rest are plain words. */
const STATE_TONE: Record<VariantState, string> = {
  draft: 'text-text-secondary',
  scheduled: 'text-text-secondary',
  deferred: 'text-warning-700',
  posting: 'text-text-secondary',
  published: 'text-text-secondary',
  unknown: 'text-warning-700',
  failed: 'text-danger-700',
  removed: 'text-text-secondary',
};

/** The state as the row says it: the plain word, and a deferral's new time with it. The post's own
 *  time is on the post's line, so an account repeats a time only where its own differs. */
function stateWords(variant: SocialVariantOut, state: VariantState): string {
  if (state === 'deferred') return w.deferredTo(whenWords(variant.next_attempt_at));
  return STATE_WORDS[state];
}

/** A post's time on its line: when it went out, the time it is scheduled for (or, once no account
 *  is still waiting to post it, the time it was scheduled for), or, for one sent at once that never
 *  went out, when it was sent. */
export function postWhen(post: SocialPostOut): string {
  const published = post.variants
    .map((variant) => variant.published_at)
    .filter((value): value is string => typeof value === 'string')
    .sort()
    .at(-1);
  if (published) return `${w.statusPublished} ${whenWords(published)}`;
  if (post.scheduled_at) {
    const waiting = post.variants.some(
      (variant) => variant.status === 'queued' || variant.status === 'publishing',
    );
    const when = whenWords(post.scheduled_at);
    return waiting ? w.scheduledFor(when) : w.wasScheduledFor(when);
  }
  return whenWords(post.created_at);
}

export function VariantRow({
  orgRef,
  variant,
  canAct,
}: {
  orgRef: string;
  variant: SocialVariantOut;
  canAct: boolean;
}) {
  const state = variantState(variant);
  const failure =
    state === 'failed' || state === 'unknown'
      ? failureWords(variant.failure_reason, variant.platform)
      : null;
  const [confirming, setConfirming] = useState(false);
  // A Retry that must be confirmed first: one that may post twice, or one past its time.
  const lateRetry = state === 'failed' && variant.failure_reason === 'too_late';
  const confirmFirst = state === 'unknown' || lateRetry;
  const refresh = useRefreshSocial(orgRef);
  const retry = useMutation({
    mutationFn: async () => {
      await sdk.orgSocialRetryOrgSocialVariant({
        path: { org_ref: orgRef, variant_id: variant.variant_id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setConfirming(false);
      toast.success(w.retried);
      await refresh();
    },
  });
  const name = variant.account_name?.trim() || platformLabel(variant.platform);
  const id = `social-variant-${variant.variant_id}`;
  return (
    <li className="flex items-start gap-10 py-8" {...testIdProp(id)}>
      <PlatformMark platform={variant.platform} size={18} className="mt-1" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-x-6 font-sans text-s13 tracking-givmo">
          <span className="font-w600 text-text-strong">{name}</span>
          <span className={cn('font-w500', STATE_TONE[state])} {...testIdProp(`${id}-state`)}>
            {stateWords(variant, state)}
          </span>
        </div>
        {state === 'deferred' ? (
          <p
            className="font-sans text-s13 tracking-givmo text-text-secondary"
            {...testIdProp(`${id}-reason`)}
          >
            {w.deferredWhy}
          </p>
        ) : null}
        {failure ? (
          <p
            className="font-sans text-s13 tracking-givmo text-text-secondary"
            {...testIdProp(`${id}-reason`)}
          >
            {state === 'failed' ? `${failure.title}. ` : ''}
            {failure.detail}
            {state === 'unknown' && variant.account_url ? (
              <>
                {' '}
                <a
                  href={variant.account_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-w500 text-brand-600 underline-offset-2 hover:underline"
                  {...testIdProp(`${id}-check`)}
                >
                  {w.checkAccount(platformLabel(variant.platform))}
                </a>
              </>
            ) : null}
          </p>
        ) : null}
        {variant.account_status === 'disconnected' &&
        (state === 'scheduled' || state === 'deferred') ? (
          <p className="font-sans text-s13 tracking-givmo text-text-secondary">{w.accountGone}</p>
        ) : null}
        {retry.isError ? (
          <ErrorMessage testId={`${id}-retry-error`}>{actionErrorWords(retry.error)}</ErrorMessage>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-8">
        {state === 'published' && variant.external_permalink ? (
          <a
            href={variant.external_permalink}
            target="_blank"
            rel="noreferrer"
            className="font-sans text-s13 font-w500 text-brand-600 underline-offset-2 hover:underline"
            {...testIdProp(`${id}-permalink`)}
          >
            {w.viewPost}
          </a>
        ) : null}
        {canAct && variant.can_retry ? (
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            loading={retry.isPending && !confirming}
            onClick={() => (confirmFirst ? setConfirming(true) : retry.mutate())}
            testId={`${id}-retry`}
          >
            {w.retry}
          </Button>
        ) : null}
      </div>
      {confirming ? (
        <Dialog.Root open onOpenChange={(next) => !next && setConfirming(false)}>
          <Dialog.Content
            aria-label={lateRetry ? w.retryLateTitle : w.retryUnknownTitle}
            width={448}
            className="flex flex-col gap-16"
            testId={`${id}-retry-confirm`}
          >
            <Dialog.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
              {lateRetry ? w.retryLateTitle : w.retryUnknownTitle}
            </Dialog.Title>
            <p className="font-sans text-s14 tracking-givmo text-text-secondary">
              {lateRetry
                ? w.retryLateBody(whenWords(variant.due_at))
                : w.retryUnknownBody(name, platformLabel(variant.platform))}
            </p>
            {!lateRetry && variant.account_url ? (
              <a
                href={variant.account_url}
                target="_blank"
                rel="noreferrer"
                className="w-fit font-sans text-s14 font-w500 text-brand-600 underline-offset-2 hover:underline"
                {...testIdProp(`${id}-retry-check`)}
              >
                {w.checkAccount(platformLabel(variant.platform))}
              </a>
            ) : null}
            <div className="flex justify-end gap-8">
              <Button appearance="saas" tone="secondary" onClick={() => setConfirming(false)}>
                {w.cancel}
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                loading={retry.isPending}
                onClick={() => retry.mutate()}
                testId={`${id}-retry-anyway`}
              >
                {lateRetry ? w.postNow : w.retryAnyway}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Root>
      ) : null}
    </li>
  );
}

/** Whether a post can be opened for editing: nothing of it has gone, is going or failed — every
 *  account's post is scheduled (or a draft), so moving the scheduled ones back makes it a draft. */
export function editableWhenScheduled(post: SocialPostOut): boolean {
  const variants = post.variants ?? [];
  return (
    variants.some((variant) => variant.status === 'queued') &&
    variants.every((variant) => variant.status === 'queued' || variant.status === 'draft')
  );
}

/** Edit a scheduled post: it goes back to a draft (Cancel, which clears its time), its time kept in
 *  this tab for the Schedule dialog, and the composer opens on it. */
export function useEditScheduled(orgRef: string, post: SocialPostOut) {
  const refresh = useRefreshSocial(orgRef);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async () => {
      await sdk.orgSocialCancelOrgSocialPost({
        path: { org_ref: orgRef, post_id: post.post_id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      keepRescheduleTime(post.post_id, post.scheduled_at);
      await refresh();
      void navigate({ to: socialPath({ page: 'post', postId: post.post_id }) });
    },
  });
}

/** The confirm before a scheduled post goes back to a draft. */
export function EditScheduledDialog({
  orgRef,
  post,
  onClose,
}: {
  orgRef: string;
  post: SocialPostOut;
  onClose: () => void;
}) {
  const edit = useEditScheduled(orgRef, post);
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !edit.isPending && onClose()}>
      <Dialog.Content
        aria-label={w.editScheduledTitle}
        width={440}
        className="flex flex-col gap-16"
        testId="social-edit-scheduled-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
          {w.editScheduledTitle}
        </Dialog.Title>
        <p className="font-sans text-s14 tracking-givmo text-text-secondary">
          {w.editScheduledBody}
        </p>
        {edit.isError ? (
          <ErrorMessage testId="social-edit-scheduled-error">
            {actionErrorWords(edit.error)}
          </ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-8">
          <Button appearance="saas" tone="secondary" disabled={edit.isPending} onClick={onClose}>
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={edit.isPending}
            onClick={() => edit.mutate()}
            testId="social-edit-scheduled-confirm"
          >
            {w.editScheduledConfirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** Cancel what is still scheduled: every queued account's post back to a draft; what already went
 *  stays, and the toast says which happened. */
export function useCancelPost(orgRef: string, post: SocialPostOut) {
  const refresh = useRefreshSocial(orgRef);
  return useMutation({
    mutationFn: async () => {
      const { data } = await sdk.orgSocialCancelOrgSocialPost({
        path: { org_ref: orgRef, post_id: post.post_id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      const allDrafts = (data.variants ?? []).every((variant) => variant.status === 'draft');
      toast.success(allDrafts ? w.cancelled : w.cancelledSome);
      await refresh();
    },
  });
}

/** A write's refusal in the module's words (the store's and Social's `error_code`s). */
export function actionErrorWords(error: unknown): string {
  const code = isGivmoError(error) ? firstErrorCode(error) : null;
  switch (code) {
    case 'company_guard_pending':
    case 'organization_ineligible':
      return `${failureWords(code, '').title}. ${failureWords(code, '').detail}`;
    case 'schedule_out_of_window':
      return w.scheduleOutOfWindow;
    case 'channel_unavailable':
      return w.channelUnavailable;
    case 'post_not_editable':
    case 'post_not_deletable':
      return w.postAlreadySent;
    case 'nothing_to_cancel':
      return w.nothingToCancel;
    case 'variant_not_retryable':
      return w.notRetryable;
    case 'image_too_large':
      return w.imageTooBig;
    case 'image_unreadable':
      return w.imageUnreadable;
    case 'media_unavailable':
      return w.mediaUnavailable;
    case 'BRAND_FILE_TYPE':
      return w.graphicNotAnImage;
    case 'COLLATERAL_OUT_OF_DATE':
      return w.graphicOutOfDate;
    default:
      return w.actionFailed;
  }
}
